/* eslint-disable no-underscore-dangle */
// Note: this sub module uses vuex-class-component: https://github.com/michaelolof/vuex-class-component

import {
  createModule, mutation, action,
} from 'vuex-class-component'
import Firebase from '@/firebase'
import { AuthUserRolesT } from '../../auth/authRoles'

/**
 * Utility functions. Returns the initials of the first and last
 * words in the provided string - or - if the string only contains
 * one word: the first two letters of that word. All in uppercase.
 *
 * @param str
 */
const getTwoLetterInitialsOrFirstLetters = (str: string) => {
  const myStr = str.trim()
  if (myStr === '') return '??'
  const strParts = myStr.split(' ')

  if (strParts.length === 1) {
    // only one word, then take the two first letters
    return myStr.substring(0, 2).toUpperCase()
  }
  // more than one word, take the first letters of the first and last word
  const res = myStr[0].charAt(0) + myStr[myStr.length - 1].charAt(0)
  return res.toUpperCase()
}

export interface AuthUserState {
  id: string;
  email: string | null;
  displayName: string | null;
  user: Firebase.User;
  roles: AuthUserRolesT;
}

const VuexModule = createModule({
  strict: false,
})

export class AuthUser extends VuexModule.With({ namespaced: 'authUser' }) {
  private _isLoggedIn = false

  private _id: string | null = null

  private _email: string | null = null

  private _displayName: string | null = null

  private _user: Firebase.User | null = null

  private _roles: AuthUserRolesT = []

  private _authIsPending = false

  @mutation setAuthIsPending(isPending: boolean) {
    this._authIsPending = isPending
  }

  @mutation setUser(authUser: AuthUserState) {
    this._isLoggedIn = true
    this._id = authUser.id
    this._email = authUser.email
    this._displayName = authUser.displayName
    this._user = authUser.user
    this._roles = authUser.roles
  }

  @mutation clearUser() {
    this._isLoggedIn = false
    this._id = null
    this._email = null
    this._displayName = null
    this._user = null
    this._roles = []
  }

  @action async doSomethingAsync() {
    return 20
  }

  @action async doAnotherAsyncStuff() {
    // return payload + this.fullName + this.fullName
  }

  get authIsPending() {
    return this._authIsPending
  }

  get mayBeLoggedIn() {
    if (this._isLoggedIn) return true
    return this._authIsPending
  }

  get isLoggedIn() {
    return this._isLoggedIn
  }

  get idToken() {
    if (this._user === null) return null
    return this._user.getIdToken()
  }

  get id() {
    return this._id
  }

  get displayName() {
    // Display name may not be set, email should always exist
    if (this._displayName !== null) return this._displayName
    if (this._email !== null) return this._email
    return 'name ??'
  }

  get avatarInitials() {
    return getTwoLetterInitialsOrFirstLetters(this.displayName)
  }

  // Define a mutation for the vuex getter.
  // NOTE this only works for getters.
  /*   set fullName(name: string) {
    const names = name.split(' ');
    [this.firstName, this.lastName] = names
  }
 */
  get email() {
    return this._email
  }

  get roles(): AuthUserRolesT {
    return this._roles
  }
}
